import axios from 'axios';

const API_BASE = '/machinesim/api/erp';

// ========== Machines ==========

export const getMachines = async () => {
  const response = await axios.get(`${API_BASE}/machines`);
  const list = response.data?.machines ?? response.data;
  return Array.isArray(list) ? list : [];
};

export const getMachine = async (id) => {
  const response = await axios.get(`${API_BASE}/machines/${id}`);
  return response.data;
};

export const createMachine = async (data) => {
  const response = await axios.post(`${API_BASE}/machines`, data);
  return response.data;
};

export const updateMachine = async (id, data) => {
  const response = await axios.put(`${API_BASE}/machines/${id}`, data);
  return response.data;
};

export const deleteMachine = async (id) => {
  const response = await axios.delete(`${API_BASE}/machines/${id}`);
  return response.data;
};

// ========== Parts ==========

export const getParts = async (companySlug = 'default') => {
  const params = new URLSearchParams();
  if (companySlug && companySlug !== 'default') params.append('company', companySlug);
  const response = await axios.get(`${API_BASE}/parts${params.toString() ? '?' + params : ''}`);
  return response.data.parts;
};

export const getPart = async (id) => {
  const response = await axios.get(`${API_BASE}/parts/${id}`);
  return response.data;
};

export const createPart = async (data) => {
  const response = await axios.post(`${API_BASE}/parts`, data);
  return response.data;
};

export const updatePart = async (id, data) => {
  const response = await axios.put(`${API_BASE}/parts/${id}`, data);
  return response.data;
};

export const deletePart = async (id) => {
  const response = await axios.delete(`${API_BASE}/parts/${id}`);
  return response.data;
};

// ========== Operations ==========

export const getOperations = async (partId) => {
  const response = await axios.get(`${API_BASE}/parts/${partId}/operations`);
  return response.data.operations;
};

export const createOperation = async (partId, data) => {
  const response = await axios.post(`${API_BASE}/parts/${partId}/operations`, data);
  return response.data;
};

export const updateOperation = async (id, data) => {
  const response = await axios.put(`${API_BASE}/operations/${id}`, data);
  return response.data;
};

export const deleteOperation = async (id) => {
  const response = await axios.delete(`${API_BASE}/operations/${id}`);
  return response.data;
};

// ========== Schedule ==========

export const getSchedule = async ({ company = 'default', anchor, horizonHours } = {}) => {
  const params = new URLSearchParams();
  if (company && company !== 'default') params.append('company', company);
  if (anchor) params.append('anchor', anchor);
  if (horizonHours) params.append('horizonHours', String(horizonHours));
  const qs = params.toString();
  const response = await axios.get(`${API_BASE}/schedule${qs ? `?${qs}` : ''}`);
  return response.data;
};

// ========== Work Orders ==========

export const getWorkOrders = async (filters = {}) => {
  const params = new URLSearchParams();
  if (filters.status)    params.append('status',    filters.status);
  if (filters.partId)    params.append('partId',    filters.partId);
  if (filters.machineId) params.append('machineId', filters.machineId);
  if (filters.company)   params.append('company',   filters.company);

  const response = await axios.get(`${API_BASE}/work-orders?${params}`);
  return response.data.workOrders;
};

export const getWorkOrder = async (id) => {
  const response = await axios.get(`${API_BASE}/work-orders/${id}`);
  return response.data;
};

export const createWorkOrder = async (data) => {
  const response = await axios.post(`${API_BASE}/work-orders`, data);
  return response.data;
};

export const updateWorkOrder = async (id, data) => {
  const response = await axios.put(`${API_BASE}/work-orders/${id}`, data);
  return response.data;
};

export const deleteWorkOrder = async (id) => {
  const response = await axios.delete(`${API_BASE}/work-orders/${id}`);
  return response.data;
};

// ========== Demand Simulator ==========

export const generateDemand = async (demandPlan, year, woPrefix) => {
  const response = await axios.post(`${API_BASE}/demand/generate`, {
    demandPlan,
    year,
    woPrefix,
  });
  return response.data;
};

export const getDemandSummary = async (year) => {
  const params = year ? `?year=${year}` : '';
  const response = await axios.get(`${API_BASE}/demand/summary${params}`);
  return response.data;
};

/** Regenerate spork demo ERP data (or dryRun preview). Body: { dryRun?, varianceProfile?, rngSeed?, woAnchor?, seedLaborTickets? } */
export const postSporkFactoryDemo = async (body = {}) => {
  const response = await axios.post(`${API_BASE}/demo/spork-factory`, body);
  return response.data;
};

/** In-place reset: spork WOs to 0 completed + RELEASED, delete their labor tickets, all simulators READY (dev guard on server). */
export const postMesDevReset = async () => {
  const response = await axios.post(`${API_BASE}/demo/mes-dev-reset`);
  return response.data;
};
